import React, { useEffect, useReducer, useMemo, useCallback } from 'react';
import FiltrosGenerico from '../UI/FiltrosGenerico.js';
import CampoEditableTexto from '../UI/campos/CampoEditableTexto.js';
import ItemLista from '../UI/ItemLista.js';
import SelectPais from '../selectores/SelectPais.js';
import SelectProvincia from '../selectores/SelectProvincia.js';
import SelectLocalidad from '../selectores/SelectLocalidad.js';
import useSearchURL from '../../hooks/useSearchURL.js';
import * as Filtros from '../constantes/Filtros.js';
import * as Funciones from '../funciones/FuncionesFiltros.js';

const reducerFiltros = (state, action) => {
  return Funciones.ReducerFiltros(Filtros.CLUBES, state, action);
};

const FiltrosClubes = (props) => {
  const searchParameters = props.searchParams;
  const [filtros, setFiltros] = useReducer(reducerFiltros, null);
  const { isBuscando, erroresSearch, searchData, resetErroresSearch } = useSearchURL();

  // Efecto para inicializar los filtros, vacío o con filtros del queryString
  useEffect(() => {
    if (searchParameters && searchParameters.size > 0) {
      const parametros = searchParameters.entries ? [...searchParameters.entries()] : [];
      const nuevoFiltro = Funciones.ArmarFiltroParametros(Filtros.CLUBES, parametros);
      setFiltros({ type: Funciones.Reducer.NUEVO_FILTRO, value: nuevoFiltro });
    } else {
      setFiltros({ type: Funciones.Reducer.INICIALIZAR, value: Filtros.CLUBES });
    }
  }, [searchParameters]);

  const buscarClubes = useCallback(
    async (filtros) => {
      setFiltros({ type: Funciones.Reducer.BUSCAR, value: false });
      resetErroresSearch();
      const bodyFiltros = Funciones.ArmarBodyFiltros(Filtros.CLUBES, filtros);
      if (bodyFiltros !== Funciones.ObjetoFiltrosVacio(Filtros.CLUBES)) {
        props.onBuscando(isBuscando);
        searchData('clubes/filtros', bodyFiltros, props.onListaCargada);
      }
    },
    [isBuscando, props, resetErroresSearch, searchData]
  );

  // Efecto para ejecutar la búsqueda cuando se activa el flag buscar
  useEffect(() => {
    if (filtros && filtros.buscar) {
      buscarClubes(filtros);
    }
  }, [filtros, buscarClubes]);

  const onFiltroElegido = useCallback((filtroElegido) => {
    setFiltros({ type: Funciones.Reducer.BOTON, value: filtroElegido });
  }, []);

  const onFiltroChange = useCallback((tipo, valor) => {
    setFiltros({ type: tipo, value: valor });
  }, []);

  const onPaisElegido = useCallback((paisElegido) => {
    setFiltros({ type: Filtros.Club.PAIS, value: paisElegido });
  }, []);

  const onProvinciaElegida = useCallback((provinciaElegida) => {
    setFiltros({ type: Filtros.Club.PROVINCIA, value: provinciaElegida });
  }, []);

  const onLocalidadElegida = useCallback((localidadElegida) => {
    setFiltros({ type: Filtros.Club.LOCALIDAD, value: localidadElegida });
  }, []);

  const handleBuscar = useCallback(
    (event) => {
      event?.preventDefault?.();
      const error = Funciones.ValidarFiltros(Filtros.CLUBES, filtros);
      if (error === '') {
        buscarClubes(filtros);
      } else {
        alert(error);
      }
    },
    [filtros, buscarClubes]
  );

  const handleLimpiar = () => {
    setFiltros({ type: Funciones.Reducer.INICIALIZAR, value: Filtros.CLUBES });
  };

  // Configuración de campos para el componente genérico
  const campos = useMemo(
    () => [
      {
        id: Filtros.Club.NOMBRE,
        nombre: 'Nombre',
        activo: filtros?.nombre?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Club.NOMBRE}
            nombre={campo.nombre}
            selected={campo.activo}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <CampoEditableTexto
            nombre={Filtros.Club.NOMBRE}
            editar={true}
            valor={filtros.nombre?.valor ?? ''}
            onChange={onFiltroChange}
            onPressEnter={handleBuscar}
          />
        ),
      },
      {
        id: Filtros.Club.PAIS,
        nombre: 'País',
        activo: filtros?.pais?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Club.PAIS}
            nombre={campo.nombre}
            selected={campo.activo}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectPais paisActual={filtros.pais?.valor} onPaisElegido={onPaisElegido} />
        ),
      },
      {
        id: Filtros.Club.PROVINCIA,
        nombre: 'Provincia',
        activo: filtros?.provincia?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Club.PROVINCIA}
            nombre={campo.nombre}
            selected={campo.activo}
            enabled={filtros?.pais?.estado && filtros?.pais?.valor?.id}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectProvincia
            pais={filtros?.pais?.estado && filtros?.pais?.valor?.id ? filtros.pais.valor : null}
            provinciaActual={filtros.provincia?.valor}
            onProvinciaElegida={onProvinciaElegida}
          />
        ),
      },
      {
        id: Filtros.Club.LOCALIDAD,
        nombre: 'Localidad',
        activo: filtros?.localidad?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Club.LOCALIDAD}
            nombre={campo.nombre}
            selected={campo.activo}
            enabled={filtros?.pais?.estado && filtros?.pais?.valor?.id}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectLocalidad
            pais={filtros?.pais?.estado && filtros?.pais?.valor?.id ? filtros.pais.valor : null}
            provincia={
              filtros?.provincia?.estado && filtros?.provincia?.valor?.id
                ? filtros.provincia.valor
                : null
            }
            localidadActual={filtros.localidad?.valor}
            onLocalidadElegida={onLocalidadElegida}
          />
        ),
      },
    ],
    [
      filtros,
      onFiltroElegido,
      onFiltroChange,
      onPaisElegido,
      onProvinciaElegida,
      onLocalidadElegida,
      handleBuscar,
    ]
  );

  if (!filtros) {
    return null;
  }

  return (
    <FiltrosGenerico
      filtros={filtros}
      campos={campos}
      onFiltroElegido={onFiltroElegido}
      onBuscar={handleBuscar}
      onLimpiar={handleLimpiar}
      isBuscando={isBuscando}
      errores={erroresSearch}
      titulo="Buscar Clubes"
      collapsibleEnMobile={true}
      initialCollapsed={props.initialCollapsed ?? false}
      hasResults={props.hasResults ?? false}
    />
  );
};

export default FiltrosClubes;
